/* Sprint 2R-2B — B2B Portal Live Tracking page.
 *
 * Shows the customer's own active shipments with live driver position.
 * Strict isolation: business_customer_id is filtered server-side, frontend
 * only displays own shipments. NEVER shows other customers / full route.
 */
import React from "react";
import { Loader2, RefreshCw, Package, MapPin, Search } from "lucide-react";
import { businessApi } from "../../lib/api";
import LiveTrackingMap from "../../components/LiveTrackingMap";
import { toast } from "sonner";

export default function BusinessLiveTrackingPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [selectedLive, setSelectedLive] = React.useState(null);
  const [search, setSearch] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await businessApi().get("/business/live/shipments");
      setItems(data.items || []);
      if (!selectedId && (data.items || []).length > 0) {
        setSelectedId(data.items[0].shipment_id);
      }
    } catch (e) {
      toast.error("Kunne ikke hente live oversigt");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [selectedId]);

  const loadSelectedLive = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await businessApi().get(`/business/live/shipments/${id}`);
      setSelectedLive(data);
    } catch (e) {
      setSelectedLive({ available: false, reason: "Kunne ikke hente live data" });
    }
  }, []);

  React.useEffect(() => { loadOverview(); }, [loadOverview]);

  React.useEffect(() => {
    if (selectedId) loadSelectedLive(selectedId);
  }, [selectedId, loadSelectedLive]);

  // Auto-poll every 30s
  React.useEffect(() => {
    if (!selectedId) return;
    const t = setInterval(() => loadSelectedLive(selectedId), 30000);
    return () => clearInterval(t);
  }, [selectedId, loadSelectedLive]);

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (it.shipment_number || "").toLowerCase().includes(q) ||
      (it.customer_reference || "").toLowerCase().includes(q) ||
      (it.delivery_city || "").toLowerCase().includes(q) ||
      (it.delivery_postal_code || "").toLowerCase().includes(q)
    );
  });

  if (loading) return (
    <div className="flex items-center gap-2 text-brand-text2">
      <Loader2 className="animate-spin" size={18}/> Indlæser live tracking...
    </div>
  );

  return (
    <div className="space-y-6" data-testid="biz-live-tracking-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-brand-dark">Live Tracking</h1>
          <p className="text-sm text-brand-text2 mt-1">
            Aktive forsendelser ude til levering. {items.length} {items.length === 1 ? "forsendelse" : "forsendelser"}.
          </p>
        </div>
        <button
          onClick={loadOverview}
          disabled={refreshing}
          className="px-4 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="biz-live-refresh-overview"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/> Genindlæs
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-brand p-8 text-center" data-testid="biz-live-empty">
          <Package size={48} className="mx-auto text-brand-text2 mb-3"/>
          <h2 className="font-display font-bold text-lg text-brand-dark mb-2">Ingen aktive forsendelser lige nu</h2>
          <p className="text-sm text-brand-text2">Når en pakke går ud til levering, kan du følge chaufføren live her.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 space-y-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2"/>
              <input
                type="text"
                placeholder="Søg forsendelse / by / ref..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-brand text-sm bg-white"
                data-testid="biz-live-search"
              />
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="biz-live-shipment-list">
              {filtered.map((it) => {
                const isStale = it.live && !it.live.available;
                const isActive = selectedId === it.shipment_id;
                return (
                  <button
                    key={it.shipment_id}
                    onClick={() => setSelectedId(it.shipment_id)}
                    className={`w-full text-left p-3 border ${isActive ? "border-brand-red bg-brand-bg2" : "border-brand bg-white hover:bg-brand-bg2"}`}
                    data-testid={`biz-live-shipment-${it.shipment_number}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-sm text-brand-dark">{it.shipment_number}</span>
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${it.live?.available ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`}></span>
                    </div>
                    <div className="text-xs text-brand-text2 mt-1 flex items-center gap-1">
                      <MapPin size={11}/> {it.delivery_postal_code} {it.delivery_city}
                    </div>
                    {it.customer_reference && (
                      <div className="text-xs text-brand-text2 mt-0.5">Ref: {it.customer_reference}</div>
                    )}
                    <div className={`text-xs mt-1 ${it.live?.available ? "text-emerald-700" : "text-amber-700"}`}>
                      {it.live?.available ? "Live position aktiv" : (it.live?.reason || "Afventer signal")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Map for selected */}
          <div className="lg:col-span-2">
            {selectedId ? (
              <div className="bg-white border border-brand p-4" data-testid="biz-live-map-wrapper">
                {(() => {
                  const selected = items.find((it) => it.shipment_id === selectedId);
                  return (
                    <>
                      <div className="mb-3 pb-3 border-b border-gray-100">
                        <div className="font-mono font-bold text-lg text-brand-dark">{selected?.shipment_number}</div>
                        <div className="text-sm text-brand-text2">
                          Til {selected?.delivery_postal_code} {selected?.delivery_city}
                          {selected?.customer_reference && ` · Ref ${selected.customer_reference}`}
                        </div>
                      </div>
                      <LiveTrackingMap
                        live={selectedLive}
                        onRefresh={() => loadSelectedLive(selectedId)}
                        refreshing={false}
                        testId="biz-live-map"
                      />
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="bg-white border border-brand p-8 text-center text-brand-text2">Vælg en forsendelse fra listen</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
